import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { payoutsService } from '@/services/payoutsService';
import type { Invoice, MonthlyPayoutEntry, PaymentStatus } from '@/types/payment';

interface PeriodSelection {
  year: number;
  month: number;
}

function periodLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

function payoutStatus(status: string): PaymentStatus {
  if (status === 'completed') return 'Paid';
  if (status === 'failed') return 'Failed';
  return 'Pending';
}

function mapPayout(entry: MonthlyPayoutEntry): Invoice[] {
  const [rawYear, rawMonth] = entry.period.split('-');
  const label = periodLabel(Number(rawYear), Number(rawMonth));
  return entry.invoices.map((item) => {
    const isRevenue = item.type === 'revenue';
    return {
      id: `${entry.id}-${item.type}`,
      invoiceNumber: `${entry.period}-${isRevenue ? 'REV' : 'FEE'}`,
      date: label,
      description: `${isRevenue ? 'Revenue share' : 'Platform fee'} — ${label}`,
      amount: item.amount,
      currency: item.currency,
      status: payoutStatus(item.status),
      documentAvailable: true,
      payoutId: entry.id,
      invoiceType: item.type,
    };
  });
}

export function useCreatorPayoutInvoices() {
  const now = new Date();
  const [period, setPeriod] = React.useState<PeriodSelection>(() => ({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  }));
  const query = useQuery({
    queryKey: ['creator-payout-invoices', period.year, period.month],
    queryFn: () => payoutsService.getInvoices(period),
  });

  return {
    invoices: (query.data?.items ?? []).flatMap(mapPayout),
    isLoading: query.isLoading,
    isError: query.isError,
    periodLabel: periodLabel(period.year, period.month),
    setPeriod,
    refetch: () => void query.refetch(),
  };
}
